/**
 * @module cercaliagl/view/VAutocomplete
 */

import $ from 'jquery';
import { MDCTextField } from '@material/textfield';
import BaseView from './BaseView';
import { i18n } from '../dictionary';
import { createUid, textRemoveHTMLTags } from '../util';

/**
 * @type {Object.<string, ?>}
 */
const Constants = {
  MIN_CHARS: 3,
  TIMEOUT: 250,
};

/**
 * @enum {string}
 */
export const EventType = {
  AUTOCOMPLETE_CHANGE: 'autocomplete-change',
  AUTOCOMPLETE_CHANGE_WITHOUT_MIN_CHARACTERS: 'autocomplete-change-without-min-characters',
  AUTOCOMPLETE_SELECTED: 'autocomplete-selected',
  AUTOCOMPLETE_ENTER: 'autocomplete-enter',
  SUGGESTIONS_CHANGED: 'suggestions-changed',
};

/**
 * @param {string} id Id element
 * @param {string} [label] Label
 * @param {boolean} [floatingLabel]
 * @return {JQuery} JQuery element
 */
function createHTML(id, label, floatingLabel = true) {
  let html;
  if (!floatingLabel) {
    html = `
      <label class="mdc-text-field mdc-text-field--filled mdc-text-field--no-label">
        <span class="mdc-text-field__ripple"></span>
        <input class="mdc-text-field__input" type="text" placeholder="${label || i18n('Address')}" aria-label="Label">
      </label>
    `;
  } else {
    const uid = `${id}${createUid()}`;
    html = `
    <label class="mdc-text-field mdc-text-field--filled">
      <span class="mdc-text-field__ripple"></span>
      <span class="mdc-floating-label" id="${uid}">${label || i18n('Address')}</span>
      <input class="mdc-text-field__input" type="text" aria-labelledby="${uid}">
      <span class="mdc-line-ripple"></span>
    </label>
    `;
  }

  html += `
    <div class="suggestions">
      <ul class="mdc-list collection mdc-elevation--z4" />
    </div>
  `;

  return $(`#${id}`)
    .addClass('v-autocomplete')
    .html(html);
}

/**
 * @classdesc
 */
class VAutocomplete extends BaseView {
  /**
   * @constructor
   * @param {string} id Input id.
   * @param {string} [label] Label
   * @param {boolean} [floatingLabel]
   */
  constructor(id, label, floatingLabel = false) {
    const jqElement = createHTML(id, label, floatingLabel);
    super(jqElement);

    /**
     * @private
     * @type {JQuery}
     */
    this.jqInput_ = this.jqElement_.find('input');

    /**
     * @private
     * @type {JQuery}
     */
    this.jqSuggestionsList_ = this.jqElement_.find('ul.mdc-list');

    /**
     * @private
     * @type {NodeJS.Timeout}
     */
    this.timeoutId_ = null;

    /**
     * @private
     * @type {import('../service/Autocomplete').GeEntitySuggest}
     */
    this.ge_ = null;

    /**
     * @private
     * @type {string}
     */
    this.text_ = null;

    /**
     * @private
     * @type {string}
     */
    this.value_ = null;

    /**
     * @private
     * @type {boolean}
     */
    this.hasFloatingLabel_ = floatingLabel;

    /**
     * @private
     * @type {Array.<import('../servers').SolrItem>}
     */
    this.elements_ = [];

    /**
     * @private
     * @type {number}
     */
    this.currentIndex_ = -1;

    /**
     * @private
     * @type {MDCTextField}
     */
    this.mdcTextField_ = null;

    this.initializeEvents_();
  }

  /**
   * @private
   */
  initializeEvents_() {
    if (!this.jqElement_) {
      return;
    }
    const jqMdcTextField = this.jqElement_.find('.mdc-text-field');
    if (!jqMdcTextField || jqMdcTextField.length === 0) {
      return;
    }
    this.jqInput_.on('input', (e) => this.handleChangeInput_(e));
    this.jqInput_.on('keyup', (e) => this.handleKeyup_(e));
    this.jqInput_.blur(() => {
      setTimeout(() => this.hideSuggestions(), 500);
    });
    this.mdcTextField_ = new MDCTextField(jqMdcTextField.get(0));
  }

  /**
   * @private
   * @param {KeyboardEvent} e Event
   */
  handleKeyup_(e) {
    const { which } = e;
    if (which === 40) {
      // keydown
      this.keydown_();
    } else if (which === 38) {
      // keyup
      this.keyup_();
    } else if (which === 13) {
      // enter
      this.keyenter_(e);
    }
  }

  /**
   * @private
   */
  keydown_() {
    const length = this.elements_.length - 1;
    if (this.currentIndex_ < length) {
      this.currentIndex_ += 1;
      this.activeSuggestElement_(this.currentIndex_);
    }
  }

  /**
   * @private
   */
  keyup_() {
    if (this.currentIndex_ > 0) {
      this.currentIndex_ -= 1;
      this.activeSuggestElement_(this.currentIndex_);
    }
  }

  /**
   * @private
   * @param {KeyboardEvent} e Event
   */
  keyenter_(e) {
    const currentIndex = this.currentIndex_ === -1 && this.elements_.length > 0 ? 0 : this.currentIndex_;
    if (currentIndex !== -1) {
      if (this.getSuggestionsListVisibility()) {
        this.selectElement_(this.elements_[currentIndex]);
      }
    } else {
      const text = e.currentTarget.value;
      this.emit(EventType.AUTOCOMPLETE_ENTER, text);
    }
  }

  /**
   * @private
   * @param {number} index Index
   */
  activeSuggestElement_(index) {
    this.jqSuggestionsList_.find('li').removeClass('active');
    this.jqSuggestionsList_.find('li').get(index).classList.add('active');
  }

  /**
   * @private
   * @param {KeyboardEvent} e Event
   */
  handleChangeInput_(e) {
    const text = e.currentTarget.value;
    this.emit(EventType.AUTOCOMPLETE_CHANGE_WITHOUT_MIN_CHARACTERS, text);
    if (text.length >= Constants.MIN_CHARS) {
      if (this.timeoutId_) {
        clearTimeout(this.timeoutId_);
        this.timeoutId_ = null;
      }

      this.timeoutId_ = setTimeout(() => {
        this.emit(EventType.AUTOCOMPLETE_CHANGE, text);
      }, Constants.TIMEOUT);
    }
  }

  /**
   * Hide suggestions list
   */
  hideSuggestions() {
    this.jqSuggestionsList_.hide();
  }

  /**
   * Show suggestions list
   */
  showSuggestions() {
    this.jqSuggestionsList_.show();
  }

  /**
   * @return {boolean} Return if suggestions list is visible
   */
  getSuggestionsListVisibility() {
    return this.jqSuggestionsList_.is(':visible');
  }

  /**
   * Empty list
   */
  empty() {
    this.elements_ = [];
    this.currentIndex_ = -1;
    this.jqSuggestionsList_.empty();
  }

  /**
   * @param {Array.<import('../servers').SolrItem>} elements SOLR elements
   */
  suggestions(elements) {
    this.empty();
    this.elements_ = elements;
    this.elements_.forEach((element, index) => {
      this.jqSuggestionsList_.append(
        $(`<li class="mdc-list-item">
          <span class="icon ${VAutocomplete.getIcon(element)}"></span>
          <span class="text">${element.text}</span>
        </li>`)
          .data('index', index)
          .click(() => this.selectElement_(element)),
      );
    });
  }

  /**
   * @private
   * @param {import('../servers').SolrItem} element Element
   */
  selectElement_(element) {
    this.emit(EventType.AUTOCOMPLETE_SELECTED, element);
    this.hideSuggestions();
  }

  /**
   * @param {string} formattedAddress Formatted address
   */
  setOption(formattedAddress) {
    this.text_ = formattedAddress;
    this.value_ = formattedAddress;
    if (this.hasFloatingLabel_) {
      const floatingLabel = this.jqElement_.find('.mdc-floating-label');
      if (floatingLabel) {
        if (formattedAddress) {
          floatingLabel.addClass('mdc-floating-label--float-above');
        } else {
          const activeLabel = this.jqElement_.find('.mdc-text-field--focused');
          if (!activeLabel || activeLabel.length === 0) {
            floatingLabel.removeClass('mdc-floating-label--float-above');
          }
        }
      }
    }
    this._renderInput();
  }

  /**
   * Clear
   */
  clear() {
    this.text_ = '';
    this.value_ = '';
    this.ge_ = null;
    this._renderInput();
  }

  /**
   * @private
   */
  _renderInput() {
    this.jqInput_.val(textRemoveHTMLTags(this.text_));
  }

  /**
   * @static
   * @param {import('../servers').SolrItem} element Element
   * @return {string} Return
   */
  static getIcon(element) {
    let icon = null;
    if (element.categoria_id) {
      icon = `icon-${element.categoria_id}`;
    } else if (element.calle_id) {
      icon = 'icon-address';
    } else if (element.localidad_id) {
      icon = 'icon-city';
    }
    return icon;
  }

  /**
   * @return {string} Input text
   */
  getValue() {
    return this.jqInput_.val();
  }

  /**
   * @param {string} text (placeholder)
   */
  setLabel(text) {
    this.jqElement_.find('label').html(text);
  }

  /**
   * Disable
   */
  disable() {
    this.mdcTextField_.getDefaultFoundation().setDisabled(true);
  }

  /**
   * Enable
   */
  enable() {
    this.mdcTextField_.getDefaultFoundation().setDisabled(false);
  }
}

export default VAutocomplete;
